import Cookies from 'js-cookie';

interface IOnDataMoreInfo {
  conversationId?: string;
  taskId?: string;
  messageId: string;
  errorMessage?: string;
  errorCode?: string;
  fromVariableSelector?: string[];
}

export interface SseMessage {
  conversation_id?: string;
  message_id?: string;
  id?: string;
  task_id?: string;
  code?: string;
  answer?: string;
  message?: string;
  metadata?: { retriever_resources?: any[] };

  // 自定义额外字段
  source?: string;
  isFirstMessage?: boolean;
  [key: string]: any;
}

enum TransferMethod {
  all = 'all',
  local_file = 'local_file',
  remote_url = 'remote_url',
}

interface TypeWithI18N<T = string> {
  en_US: T;
  zh_Hans: T;
  [key: string]: T;
}

interface FileEntity {
  id: string;
  name: string;
  size: number;
  type: string;
  progress: number;
  transferMethod: TransferMethod;
  supportFileType: string;
  originalFile?: File;
  uploadedId?: string;
  base64Url?: string;
  url?: string;
  isRemote?: boolean;
}

interface ThoughtItem {
  id: string;
  tool: string; // plugin or dataset. May has multi.
  thought: string;
  tool_input: string;
  tool_labels?: { [key: string]: TypeWithI18N };
  message_id: string;
  observation: string;
  position: number;
  files?: string[];
  message_files?: FileEntity[];
}

interface VisionFile {
  id?: string;
  type: string;
  transfer_method: TransferMethod;
  url: string;
  upload_file_id: string;
  belongs_to?: string;
}

interface CitationItem {
  refId: string;
  refType: string;
}

interface Metadata {
  retriever_resources?: CitationItem[];
  annotation_reply: {
    id: string;
    account: {
      id: string;
      name: string;
    };
  };
}

interface FileResponse {
  related_id: string;
  extension: string;
  filename: string;
  size: number;
  mime_type: string;
  transfer_method: TransferMethod;
  type: string;
  url: string;
}

export interface MessageEnd {
  id: string;
  metadata: Metadata;
  files?: FileResponse[];
}

interface MessageReplace {
  id: string;
  task_id: string;
  answer: string;
  conversation_id: string;
}

interface AnnotationReply {
  id: string;
  task_id: string;
  answer: string;
  conversation_id: string;
  annotation_id: string;
  annotation_author_name: string;
}

interface WorkflowStartedResponse {
  task_id: string;
  workflow_run_id: string;
  event: string;
  data: {
    id: string;
    workflow_id: string;
    sequence_number: number;
    created_at: number;
  };
}

interface WorkflowFinishedResponse {
  task_id: string;
  workflow_run_id: string;
  event: string;
  data: {
    id: string;
    workflow_id: string;
    status: string;
    outputs: any;
    error: string;
    elapsed_time: number;
    total_tokens: number;
    total_steps: number;
    created_at: number;
    created_by: {
      id: string;
      name: string;
      email: string;
    };
    finished_at: number;
    files?: FileResponse[];
  };
}

interface NodeStartedResponse {
  task_id: string;
  workflow_run_id: string;
  event: string;
  data: {
    id: string;
    node_id: string;
    title?: string;
    iteration_id?: string;
    parallel_run_id?: string;
    parallel_start_node_id?: string;
    node_type: string;
    index: number;
    predecessor_node_id?: string;
    inputs: any;
    created_at: number;
    extras?: any;
  };
}

enum ErrorHandleTypeEnum {
  none = 'none',
  failBranch = 'fail-branch',
  defaultValue = 'default-value',
}

interface NodeFinishedResponse {
  task_id: string;
  workflow_run_id: string;
  event: string;
  data: {
    id: string;
    node_id: string;
    iteration_id?: string;
    node_type: string;
    index: number;
    predecessor_node_id?: string;
    inputs: any;
    process_data: any;
    outputs: any;
    status: string;
    error: string;
    elapsed_time: number;
    execution_metadata: {
      total_tokens: number;
      total_price: number;
      currency: string;
      parallel_id?: string;
      parallel_start_node_id?: string;
      iteration_index?: number;
      iteration_id?: string;
      parallel_mode_run_id: string;
      error_strategy?: ErrorHandleTypeEnum;
    };
    parallel_branch_id?: string;
    parallel_start_node_id?: string;
    created_at: number;
    files?: FileResponse[];
    retry_index?: number;
  };
}

interface IterationStartedResponse {
  task_id: string;
  workflow_run_id: string;
  event: string;
  data: {
    id: string;
    node_id: string;
    metadata: {
      iterator_length: number;
      iteration_id: string;
      iteration_index: number;
    };
    created_at: number;
    extras?: any;
  };
}

interface IterationNextResponse {
  task_id: string;
  workflow_run_id: string;
  event: string;
  data: {
    id: string;
    node_id: string;
    index: number;
    output: any;
    extras?: any;
    created_at: number;
    parallel_mode_run_id: string;
    execution_metadata: {
      parallel_id?: string;
      iteration_index: number;
      parallel_mode_run_id?: string;
    };
    duration?: number;
  };
}

interface IterationFinishedResponse {
  task_id: string;
  workflow_run_id: string;
  event: string;
  data: {
    id: string;
    node_id: string;
    outputs: any;
    extras?: any;
    status: string;
    created_at: number;
    error: string;
    execution_metadata: {
      parallel_id?: string;
    };
  };
}

interface ParallelBranchStartedResponse {
  task_id: string;
  workflow_run_id: string;
  event: string;
  data: {
    parallel_id: string;
    parallel_start_node_id: string;
    parent_parallel_id: string;
    parent_parallel_start_node_id: string;
    iteration_id?: string;
    created_at: number;
  };
}

interface ParallelBranchFinishedResponse {
  task_id: string;
  workflow_run_id: string;
  event: string;
  data: {
    parallel_id: string;
    parallel_start_node_id: string;
    parent_parallel_id: string;
    parent_parallel_start_node_id: string;
    iteration_id?: string;
    status: string;
    created_at: number;
    error: string;
  };
}

interface TextChunkResponse {
  task_id: string;
  workflow_run_id: string;
  event: string;
  data: {
    text: string;
  };
}

interface TextReplaceResponse {
  task_id: string;
  workflow_run_id: string;
  event: string;
  data: {
    text: string;
  };
}

type IOnData = (message: string, isFirstMessage: boolean, moreInfo: IOnDataMoreInfo) => void;
type IOnThought = (though: ThoughtItem) => void;
type IOnFile = (file: VisionFile) => void;
type IOnMessageEnd = (messageEnd: MessageEnd) => void;
type IOnMessageReplace = (messageReplace: MessageReplace) => void;
type IOnAnnotationReply = (messageReplace: AnnotationReply) => void;
type IOnCompleted = (hasError?: boolean, errorMessage?: string) => void;
type IOnError = (msg: string, code?: string) => void;

type IOnWorkflowStarted = (workflowStarted: WorkflowStartedResponse) => void;
type IOnWorkflowFinished = (workflowFinished: WorkflowFinishedResponse) => void;
type IOnNodeStarted = (nodeStarted: NodeStartedResponse) => void;
type IOnNodeFinished = (nodeFinished: NodeFinishedResponse) => void;
type IOnIterationStarted = (workflowStarted: IterationStartedResponse) => void;
type IOnIterationNext = (workflowStarted: IterationNextResponse) => void;
type IOnNodeRetry = (nodeFinished: NodeFinishedResponse) => void;
type IOnIterationFinished = (workflowFinished: IterationFinishedResponse) => void;
type IOnParallelBranchStarted = (parallelBranchStarted: ParallelBranchStartedResponse) => void;
type IOnParallelBranchFinished = (parallelBranchFinished: ParallelBranchFinishedResponse) => void;
type IOnTextChunk = (textChunk: TextChunkResponse) => void;
type IOnTTSChunk = (messageId: string, audioStr: string, audioType?: string) => void;
type IOnTTSEnd = (messageId: string, audioStr: string, audioType?: string) => void;
type IOnTextReplace = (textReplace: TextReplaceResponse) => void;

function unicodeToChar(text: string) {
  if (!text) return '';

  return text.replace(/\\u[0-9a-f]{4}/g, (_match, p1) => {
    return String.fromCharCode(parseInt(p1, 16));
  });
}

export const ssePost = (
  url: string,
  data: Record<string, any>,
  options: {
    onData: IOnData;
    onCompleted?: IOnCompleted;
    onThought?: IOnThought;
    onMessageEnd?: IOnMessageEnd;
    onMessageReplace?: IOnMessageReplace;
    onFile?: IOnFile;
    onWorkflowStarted?: IOnWorkflowStarted;
    onWorkflowFinished?: IOnWorkflowFinished;
    onNodeStarted?: IOnNodeStarted;
    onNodeFinished?: IOnNodeFinished;
    onIterationStart?: IOnIterationStarted;
    onIterationNext?: IOnIterationNext;
    onIterationFinish?: IOnIterationFinished;
    onNodeRetry?: IOnNodeRetry;
    onParallelBranchStarted?: IOnParallelBranchStarted;
    onParallelBranchFinished?: IOnParallelBranchFinished;
    onTextChunk?: IOnTextChunk;
    onTTSChunk?: IOnTTSChunk;
    onTTSEnd?: IOnTTSEnd;
    onTextReplace?: IOnTextReplace;
  },
) => {
  const {
    onData,
    onCompleted,
    onThought,
    onFile,
    onMessageEnd,
    onMessageReplace,
    onWorkflowStarted,
    onWorkflowFinished,
    onNodeStarted,
    onNodeFinished,
    onIterationStart,
    onIterationNext,
    onIterationFinish,
    onNodeRetry,
    onParallelBranchStarted,
    onParallelBranchFinished,
    onTextChunk,
    onTTSChunk,
    onTTSEnd,
    onTextReplace,
  } = options;
  const abortController = new AbortController();
  const token = Cookies.get('token') || '';

  fetch(url, {
    method: 'POST',
    signal: abortController.signal,
    headers: new Headers({
      Authority_token: token,
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(data),
  }).then((response) => {
    if (!/^(2|3)\d{2}$/.test(String(response.status))) {
      response.json().then((data: any) => {
        onCompleted?.(true, data.message || 'Server Error');
      });
      return;
    }

    if (!response.ok) throw new Error('Network response was not ok');

    const reader = response.body?.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let bufferObj: Record<string, any>;
    let isFirstMessage = true;
    function read() {
      let hasError = false;
      reader?.read().then((result: any) => {
        if (result.done) {
          onCompleted && onCompleted(false);
          return;
        }
        buffer += decoder.decode(result.value, { stream: true });
        const lines = buffer?.split('\n') || [];
        try {
          lines.forEach((message) => {
            if (message.startsWith('data:')) {
              // check if it starts with data:
              try {
                bufferObj = JSON.parse(message.substring(5)) as Record<string, any>; // remove data: and parse as json
              } catch (e) {
                // mute handle message cut off
                onData('', isFirstMessage, {
                  conversationId: bufferObj?.conversation_id,
                  messageId: bufferObj?.message_id,
                });
                return;
              }
              if (bufferObj.status === 400 || !bufferObj.event) {
                onData('', false, {
                  conversationId: undefined,
                  messageId: '',
                  errorMessage: bufferObj?.message,
                  errorCode: bufferObj?.code,
                });
                hasError = true;
                console.log('错误1');

                onCompleted?.(true, bufferObj?.message);
                return;
              }
              if (bufferObj.event === 'message' || bufferObj.event === 'agent_message') {
                // can not use format here. Because message is splitted.
                onData(unicodeToChar(bufferObj.answer), isFirstMessage, {
                  conversationId: bufferObj.conversation_id,
                  taskId: bufferObj.task_id,
                  messageId: bufferObj.id,
                  fromVariableSelector: bufferObj.from_variable_selector,
                });
                isFirstMessage = false;
              } else if (bufferObj.event === 'agent_thought') {
                onThought?.(bufferObj as ThoughtItem);
              } else if (bufferObj.event === 'message_file') {
                onFile?.(bufferObj as VisionFile);
              } else if (bufferObj.event === 'message_end') {
                onMessageEnd?.(bufferObj as MessageEnd);
              } else if (bufferObj.event === 'message_replace') {
                onMessageReplace?.(bufferObj as MessageReplace);
              } else if (bufferObj.event === 'workflow_started') {
                onWorkflowStarted?.(bufferObj as WorkflowStartedResponse);
              } else if (bufferObj.event === 'workflow_finished') {
                onWorkflowFinished?.(bufferObj as WorkflowFinishedResponse);
              } else if (bufferObj.event === 'node_started') {
                onNodeStarted?.(bufferObj as NodeStartedResponse);
              } else if (bufferObj.event === 'node_finished') {
                onNodeFinished?.(bufferObj as NodeFinishedResponse);
              } else if (bufferObj.event === 'iteration_started') {
                onIterationStart?.(bufferObj as IterationStartedResponse);
              } else if (bufferObj.event === 'iteration_next') {
                onIterationNext?.(bufferObj as IterationNextResponse);
              } else if (bufferObj.event === 'iteration_completed') {
                onIterationFinish?.(bufferObj as IterationFinishedResponse);
              } else if (bufferObj.event === 'node_retry') {
                onNodeRetry?.(bufferObj as NodeFinishedResponse);
              } else if (bufferObj.event === 'parallel_branch_started') {
                onParallelBranchStarted?.(bufferObj as ParallelBranchStartedResponse);
              } else if (bufferObj.event === 'parallel_branch_finished') {
                onParallelBranchFinished?.(bufferObj as ParallelBranchFinishedResponse);
              } else if (bufferObj.event === 'text_chunk') {
                onTextChunk?.(bufferObj as TextChunkResponse);
              } else if (bufferObj.event === 'text_replace') {
                onTextReplace?.(bufferObj as TextReplaceResponse);
              } else if (bufferObj.event === 'tts_message') {
                onTTSChunk?.(bufferObj.message_id, bufferObj.audio, bufferObj.audio_type);
              } else if (bufferObj.event === 'tts_message_end') {
                onTTSEnd?.(bufferObj.message_id, bufferObj.audio);
              }
            } else if (message) {
              try {
                const data = JSON.parse(message);

                onData(data?.message, isFirstMessage, {
                  conversationId: undefined,
                  messageId: '',
                  errorMessage: `${data?.message}`,
                });

                onCompleted?.(true);
              } catch (error) {
                throw '';
              }
            }
          });
          buffer = lines[lines.length - 1];
        } catch (e) {
          onData('', false, {
            conversationId: undefined,
            messageId: '',
            errorMessage: `${e}`,
          });
          hasError = true;
          console.log('错误2');
          onCompleted?.(true, e as string);
          return;
        }
        if (!hasError) read();
      });
    }
    read();
  });

  return {
    abort() {
      abortController.abort();
    },
  };
};

export class Listener<T extends Record<string, (...args: any[]) => void>> {
  private eventCenter = new Map<keyof T, Set<T[keyof T]>>();

  /**
   * 侦听某个事件
   */
  on<K extends keyof T>(type: K, listener: T[K]) {
    const set = this.eventCenter.get(type) || new Set<T[K]>();

    if (this.eventCenter.has(type)) {
      set.add(listener);
    } else {
      this.eventCenter.set(type, set.add(listener));
    }
  }

  /**
   * 解除对某个事件的侦听
   */
  off<K extends keyof T>(type?: K, listener?: T[K]) {
    if (!type) {
      this.eventCenter.clear();
      return;
    }

    if (listener) {
      const set = this.eventCenter.get(type);

      set && set.delete(listener);
    } else {
      this.eventCenter.delete(type);
    }
  }

  /**
   * 触发某个侦听事件下的所有回调
   */
  fire<K extends keyof T>(type: K, ...rest: Parameters<T[K]>) {
    const set = this.eventCenter.get(type);

    set && set.forEach((callback) => callback(...(rest as Array<Parameters<T[K]>>)));
  }
}
